import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Power,
  Loader2,
  Phone,
  MapPin,
  Users,
  ShoppingCart,
  IndianRupee
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function SalesStaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [areas, setAreas] = useState([]);
  const [staffTypes, setStaffTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    phone: '',
    area: '',
    areas: [],
    is_active: true,
    expense_access: false,
    staff_type_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchStaff();
    fetchStaffTypes();
    fetchAreas();
  }, [search, statusFilter]);

  const fetchStaff = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      
      const response = await adminApi.getSalesStaff(params);
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffTypes = async () => {
    try {
      const response = await adminApi.getStaffTypes();
      setStaffTypes(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch staff types');
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await adminApi.getAreas();
      setAreas(response.data?.areas || []);
    } catch (error) {
      toast.error('Failed to fetch areas');
    }
  };

  const handleAdd = async () => {
    if (!formData.username || !formData.password || !formData.name || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!formData.areas?.length) {
      toast.error('Please assign at least one area');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.createSalesStaff(formData);
      toast.success('Sales staff created successfully');
      setShowAddModal(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.areas?.length) {
      toast.error('Please assign at least one area');
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        areas: formData.areas,
        is_active: formData.is_active,
        expense_access: formData.expense_access,
        staff_type_id: formData.staff_type_id || undefined
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await adminApi.updateSalesStaff(selectedStaff.id, updateData);
      toast.success('Sales staff updated successfully');
      setShowAddModal(false);
      setIsEditing(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (staffMember) => {
    try {
      await adminApi.toggleSalesStaffStatus(staffMember.id);
      toast.success(`Staff ${staffMember.is_active ? 'deactivated' : 'activated'}`);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewDetails = async (staffMember) => {
    try {
      const response = await adminApi.getSalesStaffById(staffMember.id);
      setSelectedStaff(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to fetch staff details');
    }
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      username: staffMember.username,
      password: '',
      name: staffMember.name,
      phone: staffMember.phone,
      area: (staffMember.areas || []).join(', '),
      areas: staffMember.areas || (staffMember.area ? staffMember.area.split(',').map((a) => a.trim()).filter(Boolean) : []),
      is_active: staffMember.is_active,
      expense_access: staffMember.expense_access || false,
      staff_type_id: staffMember.staff_type_id || ''
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      phone: '',
      area: '',
      is_active: true,
      expense_access: false,
      areas: [],
      staff_type_id: ''
    });
    setSelectedStaff(null);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6" data-testid="sales-staff-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Sales Staff</h1>
          <p className="text-[#A8A8A8] mt-1">Manage your sales team</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-[#C97A5E] hover:bg-[#B06850] text-white"
          data-testid="add-staff-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A8A8]" />
              <Input
                placeholder="Search by name, phone, or area..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 border-[#E5E5E5]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Staff Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : staff.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <Users className="w-12 h-12 mb-4" />
              <p>No sales staff found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Name</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Phone</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Area</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Customers</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Orders</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Customer Balance</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Expenses</th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {staff.map((s) => (
                    <tr key={s.id} className="hover:bg-[#FAFAF9]/50 transition-colors" data-testid={`staff-row-${s.id}`}>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#C97A5E]/10 flex items-center justify-center text-[#C97A5E] font-semibold">
                            {s.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-[#2D2420]">{s.name}</p>
                            <p className="text-sm text-[#A8A8A8]">@{s.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">{s.phone}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{(s.areas && s.areas.length ? s.areas.join(', ') : s.area) || '-'}</td>
                      <td className="py-4 px-6">
                        <Badge className={cn(
                          "text-xs",
                          s.is_active 
                            ? "bg-green-100 text-green-800" 
                            : "bg-red-100 text-red-800"
                        )}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">{s.customer_count}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{s.order_count}</td>
                      <td className="py-4 px-6">
                        <span className={cn(
                          "font-semibold",
                          s.outstanding_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                        )}>
                          {formatCurrency(s.outstanding_amount)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={cn("text-xs", s.expense_access ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700')}>
                          {s.expense_access ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(s)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`view-staff-${s.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(s)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`edit-staff-${s.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(s)}
                            className={cn(
                              "hover:bg-[#FAFAF9]",
                              s.is_active ? "text-red-600" : "text-green-600"
                            )}
                            data-testid={`toggle-staff-${s.id}`}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) resetForm(); setShowAddModal(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {isEditing ? 'Edit Sales Staff' : 'Add Sales Staff'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!isEditing && (
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="staff-username-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#5C4033]">{isEditing ? 'New Password (optional)' : 'Password *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isEditing ? 'Leave blank to keep current' : 'Enter password'}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="staff-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="staff-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Phone *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="staff-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Areas *</Label>
              <div className="rounded-lg border border-[#E5E5E5] p-3 space-y-2 max-h-40 overflow-y-auto" data-testid="staff-area-input">
                {areas.length === 0 ? (
                  <p className="text-sm text-[#A8A8A8]">No areas found. Add areas in Settings first.</p>
                ) : (
                  areas.map((areaName) => (
                    <label key={areaName} className="flex items-center gap-2 text-sm text-[#2D2420]">
                      <Checkbox
                        checked={(formData.areas || []).includes(areaName)}
                        onCheckedChange={(checked) => {
                          const set = new Set(formData.areas || []);
                          if (checked === true) set.add(areaName);
                          else set.delete(areaName);
                          const nextAreas = Array.from(set);
                          setFormData({ ...formData, areas: nextAreas, area: nextAreas.join(', ') });
                        }}
                      />
                      <span>{areaName}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Staff Type</Label>
              <Select
                value={formData.staff_type_id || ''}
                onValueChange={(value) => setFormData({ ...formData, staff_type_id: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]" data-testid="staff-type-select">
                  <SelectValue placeholder="Select staff type" />
                </SelectTrigger>
                <SelectContent>
                  {staffTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-[#E5E5E5] p-3">
              <div>
                <p className="text-sm font-medium text-[#2D2420]">Expense Access</p>
                <p className="text-xs text-[#A8A8A8]">Allow access to shared expenses</p>
              </div>
              <Switch
                checked={formData.expense_access}
                onCheckedChange={(checked) => setFormData({ ...formData, expense_access: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { resetForm(); setShowAddModal(false); }}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={isEditing ? handleUpdate : handleAdd}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              data-testid="save-staff-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Staff Details</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#C97A5E]/10 flex items-center justify-center text-[#C97A5E] text-2xl font-bold">
                  {selectedStaff.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#2D2420]">{selectedStaff.name}</h3>
                  <p className="text-[#A8A8A8]">@{selectedStaff.username}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-[#FAFAF9] rounded-xl">
                  <Phone className="w-5 h-5 text-[#C97A5E]" />
                  <div>
                    <p className="text-xs text-[#A8A8A8]">Phone</p>
                    <p className="font-medium text-[#2D2420]">{selectedStaff.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#FAFAF9] rounded-xl">
                  <MapPin className="w-5 h-5 text-[#C97A5E]" />
                  <div>
                    <p className="text-xs text-[#A8A8A8]">Area</p>
                    <p className="font-medium text-[#2D2420]">{(selectedStaff.areas && selectedStaff.areas.length ? selectedStaff.areas.join(', ') : selectedStaff.area) || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#FAFAF9] rounded-xl">
                  <Users className="w-5 h-5 text-[#C97A5E]" />
                  <div>
                    <p className="text-xs text-[#A8A8A8]">Customers</p>
                    <p className="font-medium text-[#2D2420]">{selectedStaff.customer_count}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#FAFAF9] rounded-xl">
                  <ShoppingCart className="w-5 h-5 text-[#C97A5E]" />
                  <div>
                    <p className="text-xs text-[#A8A8A8]">Orders</p>
                    <p className="font-medium text-[#2D2420]">{selectedStaff.order_count}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#FAFAF9] rounded-xl">
                  <IndianRupee className="w-5 h-5 text-[#4A7c59]" />
                  <div>
                    <p className="text-xs text-[#A8A8A8]">Expense Access</p>
                    <p className="font-medium text-[#2D2420]">{selectedStaff.expense_access ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#FAFAF9] rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IndianRupee className="w-5 h-5 text-[#E76F51]" />
                    <span className="text-[#A8A8A8]">Customer Balance</span>
                  </div>
                  <span className={cn(
                    "text-xl font-bold",
                    selectedStaff.outstanding_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                  )}>
                    {formatCurrency(selectedStaff.outstanding_amount)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
